<template>
  <section class="flex flex-col gap-4">
    <div class="flex items-center gap-3">
      <h1 class="page-title font-bold">Inicio</h1>
      <VaChip v-if="isLegal" color="info" size="small" icon="gavel">Representante Legal</VaChip>
    </div>

    <!-- Panel de Avisos Preventivos de Vencimiento de CN (PP-AO-16) -->
    <div v-if="navesConCNProximoAVencer.length > 0" class="flex flex-col gap-2 mb-2">
      <VaAlert
        v-for="nave in navesConCNProximoAVencer"
        :key="nave.id"
        color="warning"
        icon="warning"
        border="top"
        class="shadow-sm border border-amber-300"
      >
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div class="font-bold text-sm text-amber-950 flex items-center gap-2">
              ⚠️ Alerta Preventiva: Certificado de Navegabilidad (CN) Próximo a Vencer (7 días)
            </div>
            <div class="text-xs text-amber-900 mt-1">
              La embarcación <strong>{{ nave.nombre_embarcacion }}</strong> tiene su Certificado de Navegabilidad con
              fecha de vencimiento el <strong>{{ nave.fechaVencimientoStr }}</strong> (en
              <strong>{{ nave.diasRestantes }} días</strong>). Gestione oportunamente su renovación con DIRECTEMAR para
              evitar la suspensión automática de su Autorización de Operación (AO).
            </div>
          </div>
          <VaButton
            size="small"
            color="warning"
            preset="primary"
            @click="$router.push('/enrollment-check/embarcaciones')"
          >
            Ver Embarcación
          </VaButton>
        </div>
      </VaAlert>
    </div>

    <template v-if="!userRole">
      <div class="flex justify-center items-center h-full">
        <span class="text-gray-500 text-xl">Cargando...</span>
      </div>
    </template>
    <template v-else>
      <template v-if="userRole!.role === UserRoles.ADMIN || userRole!.role === UserRoles.SUPERVISOR">
        <AdminDashboard />
      </template>
      <template v-else-if="userRole!.role === UserRoles.OPERADOR">
        <OperadorDashboard />
      </template>
      <template v-else-if="userRole!.role === UserRoles.GESTOR">
        <GestorDashboard />
      </template>
      <template v-else-if="userRole!.role === UserRoles.REPRESENTANTE">
        <VaCard>
          <VaCardContent class="flex flex-col items-center gap-4 py-8">
            <VaIcon :name="isLegal ? 'gavel' : 'manage_accounts'" size="3rem" color="primary" />
            <div class="text-center">
              <p class="font-semibold text-lg">Bienvenido/a</p>
              <p v-if="isLegal" class="text-gray-500 mt-1">
                Tienes una designación como Representante Legal vigente. Selecciona el armador desde tu perfil para
                operar con sus capacidades completas.
              </p>
              <p v-else class="text-gray-500 mt-1">
                Accede a tu perfil para gestionar los armadores que representas y cambiar de cuenta.
              </p>
            </div>
            <VaButton preset="primary" @click="$router.push({ name: 'preferences' })">Ir a mi perfil</VaButton>
          </VaCardContent>
        </VaCard>
      </template>
      <template v-else>
        <template v-if="userRole!.role === UserRoles.EXTERNO && userRole!.externalSpec === ExternalSpec.ARMADOR">
          <ArmadorDashboard />
        </template>
        <template v-else-if="userRole!.role === UserRoles.EXTERNO && userRole!.externalSpec === ExternalSpec.PROVEEDOR">
          <ProveedorDashboard />
        </template>
      </template>
    </template>
  </section>
</template>
<script setup lang="ts">
import { computed, onMounted, Ref, ref } from 'vue'

import roleCapacitiesManager, {
  UserRoles,
  ExternalSpec,
  ArmadorType,
  UserRole,
  getRoleName,
} from '../../managers/identity/RoleCapacitiesManager'
import AdminDashboard from './Admin/AdminDashboard.vue'
import AdminDashboardTest from './Admin/AdminDashboardTest.vue'
import OperadorDashboard from './Operador/OperadorDashboard.vue'
import GestorDashboard from './Gestor/GestorDashboard.vue'
import ArmadorDashboard from './Armador/ArmadorDashboard.vue'
import ProveedorDashboard from './Proveedor/ProveedorDashboard.vue'

import dataStorageManager, { BucketDefaultIDs } from '../../managers/data/DataStorageManager'
import { IEmbarcacion } from '../../services/api-services/EmbarcacionesServices'
import { parseSafeDate } from '../../services/apiUtils'
import identityManager from '../../managers/identity/IdentityManager'

const userRole: Ref<UserRole | null> = roleCapacitiesManager.getRefUserRole()
// Reactivo a userRole para que el chip se actualice cuando cambia el armador activo
// (un mismo rep puede ser Apoderado de un armador y Legal de otro).
const isLegal = computed(() => userRole.value !== null && roleCapacitiesManager.isActingAsRepresentanteLegal())

interface NaveAlertaCN {
  id: number
  nombre_embarcacion: string
  fechaVencimientoStr: string
  diasRestantes: number
}

const allEmbarcaciones = dataStorageManager.subscribeBucket<IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES, () => {})

const navesConCNProximoAVencer = computed<NaveAlertaCN[]>(() => {
  const list = allEmbarcaciones.value || []
  const result: NaveAlertaCN[] = []

  for (const nave of list) {
    if (!nave.fecha_vencimiento) continue
    const parsed = parseSafeDate(nave.fecha_vencimiento)
    if (!parsed) continue
    const diffMs = parsed.getTime() - Date.now()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays > 0 && diffDays <= 7) {
      result.push({
        id: nave.id,
        nombre_embarcacion: nave.nombre_embarcacion,
        fechaVencimientoStr: parsed.toLocaleDateString('es-CL'),
        diasRestantes: diffDays,
      })
    }
  }
  return result
})
onMounted(() => {})
</script>
<style scoped></style>
